import React from 'react';
import { Box, Button, Typography, keyframes } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { SvgIconProps } from '@mui/material';

const fadeSlideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

interface OnboardingChecklistItemProps {
  step: number;
  title: string;
  description: string;
  icon: React.ComponentType<SvgIconProps>;
  statusText: string;
  isComplete: boolean;
  ctaLabel: string;
  onCtaClick: () => void;
  isLast?: boolean;
  'data-testid'?: string;
}

export const OnboardingChecklistItem: React.FC<
  OnboardingChecklistItemProps
> = ({
  step,
  title,
  description,
  icon: StepIcon,
  statusText,
  isComplete,
  ctaLabel,
  onCtaClick,
  isLast = false,
  'data-testid': testId,
}) => {
  // Stagger each step: 0.3s base + 0.1s per step
  const delay = `${0.3 + (step - 1) * 0.1}s`;

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        gap: 2,
        position: 'relative',
        pb: isLast ? 0 : 1,
        opacity: 0,
        animation: `${fadeSlideUp} 0.4s ease-out ${delay} forwards`,
      }}
    >
      {/* Timeline column */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexShrink: 0,
          width: 36,
        }}
      >
        {/* Node */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: isComplete ? 'success.main' : 'transparent',
            border: '2px solid',
            borderColor: isComplete ? 'success.main' : 'divider',
            color: isComplete ? 'success.contrastText' : 'text.secondary',
            transition: 'all 0.3s ease',
          }}
        >
          {isComplete ? (
            <CheckIcon
              sx={{ fontSize: 18 }}
              data-testid={`${testId}-complete-icon`}
            />
          ) : (
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, fontSize: '0.8rem' }}
              data-testid={`${testId}-incomplete-icon`}
            >
              {step}
            </Typography>
          )}
        </Box>

        {/* Connecting line */}
        {!isLast && (
          <Box
            sx={{
              flex: 1,
              width: '2px',
              bgcolor: isComplete ? 'success.main' : 'divider',
              opacity: isComplete ? 0.4 : 1,
              mt: 0.5,
              transition: 'background-color 0.3s ease',
            }}
          />
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, pb: isLast ? 0 : 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            minHeight: 36,
          }}
        >
          <StepIcon
            sx={{
              fontSize: 18,
              color: isComplete ? 'success.main' : 'primary.main',
              opacity: isComplete ? 0.7 : 1,
              transition: 'color 0.3s ease',
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              flex: 1,
              color: isComplete ? 'text.secondary' : 'text.primary',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              flexShrink: 0,
              fontWeight: 500,
              color: isComplete ? 'success.main' : 'text.disabled',
            }}
          >
            {statusText}
          </Typography>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mt: 0.25,
            lineHeight: 1.5,
            opacity: isComplete ? 0.6 : 0.85,
          }}
        >
          {description}
        </Typography>

        {!isComplete && (
          <Button
            size="small"
            variant="contained"
            onClick={onCtaClick}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            sx={{
              mt: 1,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
            data-testid={`${testId}-cta`}
          >
            {ctaLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
};
